import React, { useEffect, useRef, useState } from "react";
import {
  ProductSearchBar,
  SelectedProduct,
  CartItems,
  QuickAction,
  PosShortcutPanel,
  CashSaleDrawer,
  QuotationSaveModal,
  ServicesDrawer,
} from "./common";
import { IMAGE_URL, SHORTCUTS } from "../../Constant";
import { Product, Stock } from "../../Types";
import { useKeyboardShortcuts, useSystemConfig } from "../../Hook";
import { useCart } from "../../Context";
import {
  useCustomersQuery,
  useRestaurantTablesQuery,
  useCompleteQuotationMutation,
} from "../../Services";
import { message, Button } from "antd";
import {
  ExpenseDrawer,
  OrderReturnDrawer,
  InvoiceModal,
} from "./../../components";
import { DiscountMode } from "../../Enums";
import { useSectionAccess } from "../../Services";
import { PosAction } from "../../Types";

export const POSPage = () => {
  const {
    addToCart,
    isWholeSale,
    cart,
    editingQuotationId,
    editingQuotationName,
    proceedingQuotationId,
    setProceedingQuotationId,
    clearCart,
  } = useCart();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<Stock | null>(null);
  const [customPrice, setCustomPrice] = useState<number>(0);
  const [customQty, setCustomQty] = useState<number>(1);
  const [customCost, setCustomCost] = useState<number>(0);
  const [customDiscount, setCustomDiscount] = useState<number>(0);
  const [discountMode, setDiscountMode] = useState<DiscountMode>(DiscountMode.AMOUNT);
  const [tempName, setTempName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const priceInputRef = useRef<any>(null);
  const [isAddTocart, setIsAddTocart] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [returnDrawerOpen, setReturnDrawerOpen] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [expensesDrawerOpen, setExpensesDrawerOpen] = useState(false);
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [completedOrderId, setCompletedOrderId] = useState<string | null>(null);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [servicesDrawerOpen, setServicesDrawerOpen] = useState(false);

  useKeyboardShortcuts([
    {
      shortcut: SHORTCUTS.POS.CYCLE_BATCH,
      handler: () => {
        if (!selectedProduct || !shouldTriggerShortcut()) return;
        cycleBatch();
      },
    },
    {
      shortcut: SHORTCUTS.POS.QTY_UP,
      handler: () => {
        if (!selectedProduct || !shouldTriggerShortcut()) return;
        setCustomQty((p) => p + 1);
      },
    },
    {
      shortcut: SHORTCUTS.POS.QTY_DOWN,
      handler: () => {
        if (!selectedProduct || !shouldTriggerShortcut()) return;
        setCustomQty((p) => Math.max(1, p - 1));
      },
    },
    {
      shortcut: SHORTCUTS.POS.FOCUS_PRICE,
      handler: () => {
        if (!selectedProduct) return;
        const inputEl = document.querySelector(
          ".ant-input-number-input",
        ) as HTMLInputElement;
        if (inputEl) {
          inputEl.focus();
          inputEl.select();
        } else {
          priceInputRef.current?.focus();
          priceInputRef.current?.select?.();
        }
      },
    },
    {
      shortcut: SHORTCUTS.POS.DISCARD_PRODUCT,
      handler: () => {
        if (!selectedProduct || !shouldTriggerShortcut()) return;
        handleDiscard();
      },
    },
    {
      shortcut: SHORTCUTS.POS.SAVE_QUOTATION,
      options: {
        enabled: cart.length > 0,
        preventDefault: true,
        stopPropagation: true,
      },
      handler: () => {
        setQuotationModalOpen(true);
      },
    },
  ]);

  const { isAllowed } = useSectionAccess("pos");
  const { isRestaurant, isService } = useSystemConfig();

  const completeQuotationMutation = useCompleteQuotationMutation();

  const { data: customerResponse, isLoading: loadingCustomers } =
    useCustomersQuery();

  const customers = customerResponse?.data ?? [];

  // Query to fetch tables using service hook
  const {
    data: tableResponse,
    isLoading: loadingTables,
    refetch: refetchTables,
  } = useRestaurantTablesQuery({
    status: "active",
  });

  const tables = tableResponse ?? [];
  const cycleBatch = () => {
    if (
      !selectedProduct ||
      !selectedProduct.stocks ||
      selectedProduct.stocks.length === 0
    )
      return;
    const currentIndex = selectedProduct.stocks.findIndex(
      (s) => s.id === selectedBatch?.id,
    );
    const nextIndex = (currentIndex + 1) % selectedProduct.stocks.length;
    const nextBatch = selectedProduct.stocks[nextIndex];
    setSelectedBatch(nextBatch);
    setCustomPrice(
      isWholeSale ? nextBatch.wholesale_price : nextBatch.retail_price,
    );
  };

  const shouldTriggerShortcut = () => {
    const active = document.activeElement;
    if (!active) return true;

    if (active.id === "pos-search-input") {
      const searchInput = active as HTMLInputElement;
      return searchInput.value.trim() === "";
    }

    const tagName = active.tagName.toLowerCase();
    const isEditing =
      tagName === "input" ||
      tagName === "textarea" ||
      active.hasAttribute("contenteditable") ||
      active.getAttribute("role") === "spinbutton" ||
      active.classList.contains("ant-select-selection-search-input") ||
      active.classList.contains("ant-input-number-input");

    return !isEditing;
  };

  const handleProductSelect = (product: Product) => {
    let mainImage = "";
    try {
      const imgs = JSON.parse(product.image_paths || "[]");
      if (imgs.length > 0) {
        mainImage = `${IMAGE_URL}/${imgs[0]}`;
      }
    } catch {
      console.error("Invalid image path");
    }

    const defaultStock =
      product.stocks?.find((s) => s.is_default === 1) ||
      product.stocks?.[0] ||
      null;

    setSelectedProduct({
      ...product,
      image_paths: mainImage,
    });
    setSelectedBatch(defaultStock);
    setCustomPrice(
      defaultStock
        ? isWholeSale
          ? defaultStock.wholesale_price
          : defaultStock.retail_price
        : product.mrp || 0,
    );
    setCustomQty(1);
    setCustomCost(0);
    setCustomDiscount(0);
    setTempName(product.is_tempory_item ? product.title : "");
    setDescription("");
  };

  const handleConfirmAdd = () => {
    if (!selectedProduct) return;
    const isTemp = selectedProduct?.is_tempory_item;

    if (!isTemp && !selectedBatch) return;

    const isNegativeAllowed = selectedProduct?.is_negative_qty_allowed === 1;
    if (
      !isTemp &&
      ((customQty > (selectedBatch?.quantity ?? 0) && !isNegativeAllowed) ||
        customPrice === 0)
    ) {
      message.warning("Invalid quantity or price");
      return;
    }

    const subtotal = customPrice * customQty;
    const discountAmount =
      discountMode === DiscountMode.PERCENT
        ? Math.min((subtotal * customDiscount) / 100, subtotal)
        : Math.min(customDiscount * customQty, subtotal);

    const batch = selectedBatch || {
      batch_no: `BATCH-TEMP-${Date.now()}`,
      cost_price: customCost,
    };

    const productPayload = {
      id: selectedProduct.id,
      title:
        selectedProduct.is_tempory_item && tempName.trim()
          ? tempName.trim()
          : selectedProduct.title,
      image: selectedProduct.image_paths,
    };

    if (isAddTocart) {
      addToCart(
        productPayload,
        batch,
        customPrice,
        customQty,
        selectedProduct,
        selectedProduct.is_tempory_item ? customCost : undefined,
        description,
        undefined,
        undefined,
        discountAmount,
        undefined,
        customDiscount,
        discountMode,
      );
      setSelectedProduct(null);
      setSelectedBatch(null);
      setCustomCost(0);
      setCustomDiscount(0);
      setDescription("");
    } else {
      setSelectedProduct(selectedProduct);
      setSelectedBatch(selectedBatch);
      setCustomPrice(customPrice);
      setCustomQty(customQty);
    }
  };

  const handleDiscard = () => {
    setSelectedProduct(null);
    setSelectedBatch(null);
    setCustomDiscount(0);
    setDescription("");
  };

  useEffect(() => {
    setIsAddTocart(!!selectedProduct);
  }, [selectedProduct]);

  useEffect(() => {
    if (selectedProduct && selectedBatch && !selectedProduct.is_tempory_item) {
      setCustomPrice(
        isWholeSale
          ? selectedBatch.wholesale_price
          : selectedBatch.retail_price,
      );
    }
  }, [isWholeSale, selectedBatch, selectedProduct]);

  useEffect(() => {
    cart?.length == 0 && refetchTables();
  }, [cart]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 text-gray-800 font-sans">
      <main className="flex-1 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 flex flex-col gap-6">
          {editingQuotationId && (
            <div className="mx-5 mt-5 bg-indigo-50 border border-indigo-200 text-indigo-800 px-4 py-3 rounded-xl flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-indigo-500">
                  request_quote
                </span>
                <span className="text-xs font-semibold">
                  Editing Quotation:{" "}
                  <strong className="underline font-bold">
                    {editingQuotationName || `Quotation #${editingQuotationId}`}
                  </strong>
                </span>
              </div>
              <Button
                size="small"
                danger
                type="primary"
                onClick={clearCart}
                className="text-[10px] h-7 font-bold rounded-lg bg-red-600 hover:bg-red-700 border-none flex items-center"
              >
                Cancel Edit
              </Button>
            </div>
          )}
          <div className="pt-5 px-5 pb-0 mb-0 flex items-center justify-between">
            <ProductSearchBar
              onSelectProduct={handleProductSelect}
              handleConfirmAdd={handleConfirmAdd}
              selectedProduct={selectedProduct}
            />
          </div>
          {selectedProduct && (
            <SelectedProduct
              product={selectedProduct}
              selectedBatch={selectedBatch}
              setSelectedBatch={setSelectedBatch}
              customPrice={customPrice}
              setCustomPrice={setCustomPrice}
              customQty={customQty}
              setCustomQty={setCustomQty}
              priceInputRef={priceInputRef}
              handleConfirmAdd={handleConfirmAdd}
              onDiscard={handleDiscard}
              customCost={customCost}
              setCustomCost={setCustomCost}
              tempName={tempName}
              setTempName={setTempName}
              description={description}
              setDescription={setDescription}
              customDiscount={customDiscount}
              setCustomDiscount={setCustomDiscount}
              discountMode={discountMode}
              setDiscountMode={setDiscountMode}
            />
          )}
          <PosShortcutPanel onSelectProduct={handleProductSelect} />
        </section>
        <CartItems
          setSelectedProduct={(product: any) => {
            setSelectedProduct(product);
            if (product?.is_tempory_item) {
              setTempName(product.title || "");
            } else {
              setTempName("");
            }
          }}
          setSelectedBatch={setSelectedBatch}
          setCustomPrice={setCustomPrice}
          setCustomQty={setCustomQty}
          setCustomCost={setCustomCost}
          setDescription={setDescription}
          setCustomDiscount={setCustomDiscount}
          setDiscountMode={setDiscountMode}
          onToggleDrawer={() => setDrawerOpen((prev) => !prev)}
          onOpenToPay={() => setPayModalOpen(true)}
          onOpenReturn={() => setReturnDrawerOpen(true)}
          onOpenExpenses={() => setExpensesDrawerOpen(true)}
          customers={customers}
          tables={tables}
          loadingCustomers={loadingCustomers}
          loadingTables={loadingTables}
          isRestaurant={isRestaurant}
        />
      </main>
      <QuickAction
        refetchTables={refetchTables}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpenReturn={() => {
          setDrawerOpen(false);
          setReturnDrawerOpen(true);
        }}
        onOpenExpenses={() => {
          setDrawerOpen(false);
          setExpensesDrawerOpen(true);
        }}
        customers={customers}
        tables={tables}
        loadingCustomers={loadingCustomers}
        loadingTables={loadingTables}
        isRestaurant={isRestaurant}
        isService={isService}
        onOpenServices={() => {
          setDrawerOpen(false);
          setServicesDrawerOpen(true);
        }}
        onSaveQuotationClick={() => {
          setDrawerOpen(false);
          setQuotationModalOpen(true);
        }}
      />
      <OrderReturnDrawer
        open={returnDrawerOpen}
        onClose={() => setReturnDrawerOpen(false)}
      />
      <ExpenseDrawer
        open={expensesDrawerOpen}
        onClose={() => setExpensesDrawerOpen(false)}
      />
      <CashSaleDrawer
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        onCheckoutSuccess={(orderId) => {
          setCompletedOrderId(orderId);
          setInvoiceModalOpen(true);
        }}
      />
      {isAllowed(PosAction.PrintReceipt) && (
        <InvoiceModal
          open={invoiceModalOpen}
          orderId={completedOrderId}
          onClose={() => {
            if (proceedingQuotationId) {
              completeQuotationMutation.mutate(proceedingQuotationId, {
                onSuccess: (res) => {
                  if (res?.success) {
                    message.success("Quotation marked as completed.");
                  }
                },
              });
              setProceedingQuotationId(null);
            }
            setInvoiceModalOpen(false);
            setCompletedOrderId(null);
          }}
        />
      )}
      <QuotationSaveModal
        open={quotationModalOpen}
        onCancel={() => setQuotationModalOpen(false)}
      />
      <ServicesDrawer
        open={servicesDrawerOpen}
        onClose={() => setServicesDrawerOpen(false)}
      />
    </div>
  );
};
